import React, { useState, useEffect } from "react";
import * as pdfjsLib from "pdfjs-dist";
import Tesseract from "tesseract.js";

// Configure pdf.js worker using CDN
pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";

// Document Verification Component
const DocumentVerification = ({
  file,
  requirementType,
  onVerificationComplete,
  isForeignStudent = false,
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [verificationDetails, setVerificationDetails] = useState(null);

  useEffect(() => {
    if (file) {
      verifyDocument(file);
    }
  }, [file]);

  const logBorderlineCase = (requirementType, confidence, details) => {
    console.log(
      `Borderline case for ${requirementType}: Confidence=${confidence}%, Details=${JSON.stringify(
        details
      )}`
    );
  };

  const validateFileBasics = (fileType, fileSize, fileExtension) => {
    const validTypes = ["image/png", "image/jpeg", "application/pdf"];
    if (!validTypes.includes(fileType)) {
      return {
        valid: false,
        message: "Only PNG, JPG, or PDF files are allowed.",
      };
    }

    if (fileSize > 10 * 1024 * 1024) {
      return {
        valid: false,
        message: "File size must not exceed 10MB.",
      };
    }

    const validExtensions = ["png", "jpg", "jpeg", "pdf"];
    if (!validExtensions.includes(fileExtension.toLowerCase())) {
      return {
        valid: false,
        message: "Invalid file extension. Only PNG, JPG, or PDF are allowed.",
      };
    }

    return { valid: true };
  };

  const processPdfDocument = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      const pageCount = pdf.numPages;
      if (pageCount < 1) {
        throw new Error("PDF is empty or has no valid pages");
      }

      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1.5 });

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;

      const ocrPromise = Tesseract.recognize(canvas.toDataURL(), "eng", {
        tessedit_char_whitelist:
          "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789.,;:'\"-()!? ",
        tessedit_pageseg_mode: "6",
      });
      const timeoutPromise = new Promise((resolve, reject) =>
        setTimeout(() => reject(new Error("OCR timeout")), 30000)
      );
      const {
        data: { text },
      } = await Promise.race([ocrPromise, timeoutPromise]);

      const processedText = text ? text.replace(/\s+/g, " ").trim() : "";

      canvas.remove();
      URL.revokeObjectURL(canvas.toDataURL());

      return {
        textContent: processedText,
        pageCount,
        dimensions: { width: viewport.width, height: viewport.height },
      };
    } catch (error) {
      console.error("PDF processing error:", error);
      throw new Error(`Error processing PDF: ${error.message}`);
    }
  };

  const processImageDocument = async (file) => {
    try {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      await new Promise((resolve) => (img.onload = resolve));

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      context.drawImage(img, 0, 0);

      const enhancedCanvas = document.createElement("canvas");
      const enhancedContext = enhancedCanvas.getContext("2d");
      enhancedCanvas.width = canvas.width;
      enhancedCanvas.height = canvas.height;

      enhancedContext.filter = "contrast(1.2) brightness(1.1)";
      enhancedContext.drawImage(canvas, 0, 0);

      const ocrPromise = Tesseract.recognize(
        enhancedCanvas.toDataURL(),
        "eng",
        {
          tessedit_char_whitelist:
            "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789.,;:'\"-()!? ",
          tessedit_pageseg_mode: "6",
        }
      );
      const timeoutPromise = new Promise((resolve, reject) =>
        setTimeout(() => reject(new Error("OCR timeout")), 30000)
      );
      const {
        data: { text },
      } = await Promise.race([ocrPromise, timeoutPromise]);

      const processedText = text ? text.replace(/\s+/g, " ").trim() : "";

      canvas.remove();
      enhancedCanvas.remove();
      URL.revokeObjectURL(img.src);

      return {
        textContent: processedText,
        dimensions: { width: img.width, height: img.height },
      };
    } catch (error) {
      console.error("Image processing error:", error);
      throw new Error(`Error processing image: ${error.message}`);
    }
  };

  const verifyBirthCertificate = async (documentData) => {
    try {
      const { textContent } = documentData;
      if (!textContent || textContent.length < 10) {
        return {
          isValid: true,
          message:
            "Document accepted with minimal verification. Please ensure this is a valid birth certificate.",
          details: {
            confidence: "40%",
            note: "Limited text detected but accepted",
          },
        };
      }

      const textLower = textContent.toLowerCase();

      let primaryKeywords = [
        "birth certificate",
        "certificate of birth",
        "certificate of live birth",
        "birth record",
        "vital record",
        "civil registry",
        "registrar",
      ];

      let secondaryKeywords = [
        "born",
        "date of birth",
        "place of birth",
        "sex",
        "gender",
        "father",
        "mother",
        "parent",
        "name",
        "nationality",
        "registration number",
        "issued",
        "certified",
        "authenticated",
      ];

      if (!isForeignStudent) {
        primaryKeywords.push(
          "psa",
          "nso",
          "philippine statistics authority",
          "national statistics office",
          "republic of the philippines",
          "colb",
          "municipal registrar",
          "civil registration service"
        );
        secondaryKeywords.push(
          "civil registrar",
          "civil registry",
          "signature",
          "local civil registrar",
          "registered",
          "birth record",
          "registry number",
          "delivery",
          "attendant"
        );
      } else {
        primaryKeywords.push(
          "ministry",
          "government",
          "embassy",
          "consulate",
          "apostille",
          "legalized"
        );
        secondaryKeywords.push(
          "country",
          "state",
          "province",
          "district",
          "county",
          "foreign affairs",
          "notarized",
          "translation",
          "translator",
          "certification"
        );
      }

      const primaryMatches = primaryKeywords.filter((keyword) =>
        textLower.includes(keyword)
      );
      const secondaryMatches = secondaryKeywords.filter((keyword) =>
        textLower.includes(keyword)
      );

      const primaryScore = primaryMatches.length * 25;
      const secondaryScore = secondaryMatches.length * 5;
      const confidence = Math.min(100, primaryScore + secondaryScore);

      if (confidence >= 40 || primaryMatches.length > 0) {
        return {
          isValid: true,
          message: "Birth certificate verification successful.",
          details: {
            confidence: `${confidence}%`,
            primaryKeywordsFound: primaryMatches,
            secondaryKeywordsFound: secondaryMatches,
            isForeign: isForeignStudent,
          },
        };
      } else if (confidence >= 20) {
        return {
          isValid: true,
          message:
            "Document appears to be a birth certificate, but with limited confidence. Please verify its contents.",
          details: {
            confidence: `${confidence}%`,
            primaryKeywordsFound: primaryMatches,
            secondaryKeywordsFound: secondaryMatches,
            note: "Accepted with limited confidence",
            isForeign: isForeignStudent,
          },
        };
      } else {
        return {
          isValid: false,
          message:
            "Document does not appear to be a valid birth certificate. Please check and upload the correct document.",
          details: {
            confidence: `${confidence}%`,
            primaryKeywordsFound: primaryMatches,
            secondaryKeywordsFound: secondaryMatches,
            isForeign: isForeignStudent,
          },
        };
      }
    } catch (error) {
      return {
        isValid: true,
        message:
          "Birth certificate accepted with limited verification due to processing error.",
        details: {
          error: error.message,
          note: "Automatic approval due to leniency policy",
          isForeign: isForeignStudent,
        },
      };
    }
  };

  const verifyGoodMoral = async (documentData) => {
    try {
      const { textContent } = documentData;
      if (!textContent || textContent.length < 10) {
        return {
          isValid: true,
          message:
            "Document accepted with minimal verification. Please ensure this is a valid good moral certificate.",
          details: {
            confidence: "40%",
            note: "Limited text detected but accepted",
          },
        };
      }

      const textLower = textContent.toLowerCase();

      const primaryKeywords = [
        "certificate of good moral",
        "good moral character",
        "good conduct",
        "good standing",
        "moral character",
        "behavior",
        "conduct",
        "clearance",
        "no disciplinary action",
      ];

      const secondaryKeywords = [
        "school",
        "university",
        "college",
        "academy",
        "institute",
        "institution",
        "principal",
        "registrar",
        "director",
        "dean",
        "head",
        "guidance",
        "counselor",
        "issued",
        "certify",
        "attest",
        "hereby",
        "disciplinary",
        "record",
        "academic",
        "student",
        "enrollment",
        "semester",
      ];

      const primaryMatches = primaryKeywords.filter((keyword) =>
        textLower.includes(keyword)
      );
      const secondaryMatches = secondaryKeywords.filter((keyword) =>
        textLower.includes(keyword)
      );

      const primaryScore = primaryMatches.length * 25;
      const secondaryScore = secondaryMatches.length * 5;
      const confidence = Math.min(100, primaryScore + secondaryScore);

      if (confidence >= 35 || primaryMatches.length > 0) {
        return {
          isValid: true,
          message: "Certificate of good moral verification successful.",
          details: {
            confidence: `${confidence}%`,
            primaryKeywordsFound: primaryMatches,
            secondaryKeywordsFound: secondaryMatches,
          },
        };
      } else if (confidence >= 15) {
        return {
          isValid: true,
          message:
            "Document appears to be a certificate of good moral, but with limited confidence. Please verify its contents.",
          details: {
            confidence: `${confidence}%`,
            primaryKeywordsFound: primaryMatches,
            secondaryKeywordsFound: secondaryMatches,
            note: "Accepted with limited confidence",
          },
        };
      } else {
        return {
          isValid: false,
          message:
            "Document does not appear to be a valid certificate of good moral. Please check and upload the correct document.",
          details: {
            confidence: `${confidence}%`,
            primaryKeywordsFound: primaryMatches,
            secondaryKeywordsFound: secondaryMatches,
          },
        };
      }
    } catch (error) {
      return {
        isValid: true,
        message:
          "Certificate of good moral accepted with limited verification due to processing error.",
        details: {
          error: error.message,
          note: "Automatic approval due to leniency policy",
        },
      };
    }
  };

  const verifyBaptismalOrLetter = async (documentData) => {
    try {
      const { textContent } = documentData;
      if (!textContent || textContent.length < 10) {
        return {
          isValid: true,
          message:
            "Document accepted with minimal verification. Please ensure this is a valid baptismal certificate or non-Catholic letter.",
          details: {
            confidence: "40%",
            note: "Limited text detected but accepted",
          },
        };
      }

      const textLower = textContent.toLowerCase();

      const baptismalPrimaryKeywords = [
        "baptismal certificate",
        "certificate of baptism",
        "baptism",
        "christening",
        "sacrament of baptism",
        "holy baptism",
        "baptized",
        "confirmation",
      ];

      const baptismalSecondaryKeywords = [
        "church",
        "parish",
        "priest",
        "minister",
        "pastor",
        "chapel",
        "cathedral",
        "diocese",
        "archdiocese",
        "bishop",
        "godparent",
        "godfather",
        "godmother",
        "sacrament",
        "date of baptism",
        "born",
        "catholic",
        "christian",
        "faith",
        "religious",
        "ceremony",
        "received",
        "rite",
        "baptismal record",
      ];

      const letterPrimaryKeywords = [
        "non-catholic",
        "non catholic",
        "not catholic",
        "religious exemption",
        "religious preference",
        "alternate religion",
        "exemption from",
        "parental consent",
        "guardian consent",
        "parent permission",
        "statement of faith",
      ];

      const letterSecondaryKeywords = [
        "letter",
        "signed",
        "parent",
        "guardian",
        "allow",
        "permission",
        "consent",
        "authorize",
        "exempt",
        "faith",
        "belief",
        "practice",
        "religious",
        "religion",
        "spiritual",
        "church",
        "denomination",
        "worship",
        "education",
        "instruction",
        "teachings",
        "values",
        "member",
      ];

      const baptismalPrimaryMatches = baptismalPrimaryKeywords.filter(
        (keyword) => textLower.includes(keyword)
      );
      const baptismalSecondaryMatches = baptismalSecondaryKeywords.filter(
        (keyword) => textLower.includes(keyword)
      );
      const letterPrimaryMatches = letterPrimaryKeywords.filter((keyword) =>
        textLower.includes(keyword)
      );
      const letterSecondaryMatches = letterSecondaryKeywords.filter((keyword) =>
        textLower.includes(keyword)
      );

      const baptismalPrimaryScore = baptismalPrimaryMatches.length * 25;
      const baptismalSecondaryScore = baptismalSecondaryMatches.length * 5;
      const letterPrimaryScore = letterPrimaryMatches.length * 25;
      const letterSecondaryScore = letterSecondaryMatches.length * 5;

      const baptismalConfidence = Math.min(
        100,
        baptismalPrimaryScore + baptismalSecondaryScore
      );
      const letterConfidence = Math.min(
        100,
        letterPrimaryScore + letterSecondaryScore
      );

      if (baptismalConfidence >= 30 || baptismalPrimaryMatches.length > 0) {
        return {
          isValid: true,
          message: "Baptismal certificate verification successful.",
          details: {
            confidence: `${baptismalConfidence}%`,
            documentType: "baptismal_certificate",
            primaryKeywordsFound: baptismalPrimaryMatches,
            secondaryKeywordsFound: baptismalSecondaryMatches,
          },
        };
      } else if (letterConfidence >= 30 || letterPrimaryMatches.length > 0) {
        return {
          isValid: true,
          message: "Non-Catholic letter verification successful.",
          details: {
            confidence: `${letterConfidence}%`,
            documentType: "non_catholic_letter",
            primaryKeywordsFound: letterPrimaryMatches,
            secondaryKeywordsFound: letterSecondaryMatches,
          },
        };
      } else if (baptismalConfidence >= 15 || letterConfidence >= 15) {
        const highestConfidence = Math.max(
          baptismalConfidence,
          letterConfidence
        );
        const documentType =
          baptismalConfidence >= letterConfidence
            ? "baptismal certificate"
            : "non-Catholic letter";

        return {
          isValid: true,
          message: `Document appears to be a ${documentType}, but with limited confidence. Please verify its contents.`,
          details: {
            confidence: `${highestConfidence}%`,
            documentType:
              baptismalConfidence >= letterConfidence
                ? "baptismal_certificate"
                : "non_catholic_letter",
            baptismalKeywords: [
              ...baptismalPrimaryMatches,
              ...baptismalSecondaryMatches,
            ],
            letterKeywords: [
              ...letterPrimaryMatches,
              ...letterSecondaryMatches,
            ],
            note: "Accepted with limited confidence",
          },
        };
      } else if (
        baptismalSecondaryMatches.length >= 3 ||
        letterSecondaryMatches.length >= 3
      ) {
        const moreKeywords =
          baptismalSecondaryMatches.length >= letterSecondaryMatches.length
            ? "baptismal certificate"
            : "non-Catholic letter";

        return {
          isValid: true,
          message: `Document contains some relevant keywords for a ${moreKeywords}. Accepted with minimal confidence.`,
          details: {
            confidence: `${Math.max(baptismalConfidence, letterConfidence)}%`,
            documentType:
              baptismalSecondaryMatches.length >= letterSecondaryMatches.length
                ? "baptismal_certificate"
                : "non_catholic_letter",
            baptismalKeywords: [
              ...baptismalPrimaryMatches,
              ...baptismalSecondaryMatches,
            ],
            letterKeywords: [
              ...letterPrimaryMatches,
              ...letterSecondaryMatches,
            ],
            note: "Accepted with minimal confidence based on secondary keywords",
          },
        };
      } else {
        return {
          isValid: false,
          message:
            "Document does not appear to be a valid baptismal certificate or non-Catholic letter. Please check and upload the correct document.",
          details: {
            baptismalConfidence: `${baptismalConfidence}%`,
            letterConfidence: `${letterConfidence}%`,
            baptismalKeywords: [
              ...baptismalPrimaryMatches,
              ...baptismalSecondaryMatches,
            ],
            letterKeywords: [
              ...letterPrimaryMatches,
              ...letterSecondaryMatches,
            ],
          },
        };
      }
    } catch (error) {
      return {
        isValid: true,
        message:
          "Document accepted with limited verification due to processing error.",
        details: {
          error: error.message,
          note: "Automatic approval due to leniency policy",
        },
      };
    }
  };

  const verifyEducationalDocument = async (documentData) => {
    try {
      const { textContent, pageCount } = documentData;

      if (!textContent) {
        return {
          isValid: false,
          message: 'Document is empty or unreadable. Please upload a document with visible text.',
          details: { error: 'No content detected' },
        };
      }

      const textLower = textContent.toLowerCase().replace(/\s+/g, ' ').trim();

      const educationalKeywords = [
        'school', 'college', 'university', 'institute', 'education', 'academic',
        'report card', 'grade', 'marks', 'semester', 'quarter', 'term',
        'performance', 'subject', 'teacher', 'evaluation', 'assessment',
        'rating', 'achievement', 'grading', 'period', 'module', 'level',
        'transcript', 'academic record', 'credits', 'units', 'course',
        'cumulative', 'gpa', 'grade point', 'official records', 'certification',
        'school year', 'transfer', 'subjects taken', 'enrollment', 'registration',
        'mathematics', 'math', 'science', 'english', 'history', 'physics',
        'chemistry', 'biology', 'literature', 'philosophy', 'psychology',
        'economics', 'computer', 'programming',
        'passed', 'failed', 'incomplete', 'withdrawn', 'record', 'transcript',
        'academic standing', 'academic year', 'academic performance', 'grades',
        'grade', 'grade point average', 'gpa', 'credits', 'credit', 'units',
        'course', 'coursework', 'coursework grade', 'course grade', 'grades',
        'grades received', 'grade received', 'grade point', 'gp', 'gpa', 'secondary',
        'secondary school', 'high school', 'high school education', 'general average',
        'filipino', 'english', 'mathematics', 'science', 'araling panlipunan', 'mapeh',
        'values education', 'math', 'science', 'computer', 'programming', 'passed',
        'lrn', 'learner reference number', 'school id', 'school identification number',
        'deped', 'department of education',
      ];

      const coursePatterns = /[A-Z]{2,4}[-\s]?\d{3}/g;
      const gradePatterns = /([A-F][+-]?)|(100|\d{1,2}(\.\d+)?%?)|\b(excellent|good|satisfactory|pass|fail)\b/gi;

      const keywordMatches = educationalKeywords.filter((keyword) => textLower.includes(keyword));
      const courses = textLower.match(coursePatterns) || [];
      const grades = textLower.match(gradePatterns) || [];

      let confidence = 0;

      const keywordScore = Math.min(30, keywordMatches.length * 3);
      confidence += keywordScore;

      const courseScore = Math.min(20, courses.length * 8);
      confidence += courseScore;

      const gradeScore = Math.min(40, grades.length * 6);
      confidence += gradeScore;

      const pageScore = Math.min(10, (pageCount || 1) * 5);
      confidence += pageScore;

      if (confidence >= 50) {
        return {
          isValid: true,
          message: 'Educational document verification successful.',
          details: {
            confidence: `${confidence}%`,
            keywordsFound: keywordMatches.length > 0 ? keywordMatches.slice(0, 5) : 'None',
            coursesDetected: courses.length,
            gradesDetected: grades.length,
            pageCount: pageCount || 1,
          },
        };
      } else {
        return {
          isValid: false,
          message: 'Document does not appear to be a valid educational record. Ensure it includes grades, subjects, or academic terms like "semester" or "course."',
          details: {
            confidence: `${confidence}%`,
            keywordsFound: keywordMatches.length > 0 ? keywordMatches.slice(0, 5) : 'None',
            coursesDetected: courses.length,
            gradesDetected: grades.length,
            keywordsNeeded: 'Missing key indicators like grades, subjects, or academic terms',
          },
        };
      }
    } catch (error) {
      console.error('Educational document verification error:', error);
      return {
        isValid: true,
        message: 'Educational document accepted with limited verification due to processing error.',
        details: { error: error.message, note: 'Automatic approval due to leniency policy' },
      };
    }
  };

  const verifyStudentVisa = async (documentData) => {
    try {
      const { textContent } = documentData;
      if (!textContent || textContent.length < 10) {
        return {
          isValid: true,
          message:
            "Document accepted with minimal verification. Please ensure this is a valid student visa.",
          details: {
            confidence: "40%",
            note: "Limited text detected but accepted",
          },
        };
      }

      const textLower = textContent.toLowerCase();

      const primaryKeywords = [
        "student visa",
        "visa",
        "study permit",
        "international student",
        "immigration",
        "residence permit",
      ];

      const secondaryKeywords = [
        "passport",
        "validity",
        "expiration",
        "issue date",
        "entry",
        "stay",
        "country",
        "government",
        "embassy",
        "consulate",
        "ministry",
        "foreign affairs",
        "authorization",
        "permit",
        "name",
        "nationality",
        "education",
        "school",
        "university",
        "institution",
      ];

      const primaryMatches = primaryKeywords.filter((keyword) =>
        textLower.includes(keyword)
      );
      const secondaryMatches = secondaryKeywords.filter((keyword) =>
        textLower.includes(keyword)
      );

      const primaryScore = primaryMatches.length * 25;
      const secondaryScore = secondaryMatches.length * 5;
      const confidence = Math.min(100, primaryScore + secondaryScore);

      if (confidence >= 40 || primaryMatches.length > 0) {
        return {
          isValid: true,
          message: "Student visa verification successful.",
          details: {
            confidence: `${confidence}%`,
            primaryKeywordsFound: primaryMatches,
            secondaryKeywordsFound: secondaryMatches,
          },
        };
      } else if (confidence >= 20) {
        return {
          isValid: true,
          message:
            "Document appears to be a student visa, but with limited confidence. Please verify its contents.",
          details: {
            confidence: `${confidence}%`,
            primaryKeywordsFound: primaryMatches,
            secondaryKeywordsFound: secondaryMatches,
            note: "Accepted with limited confidence",
          },
        };
      } else {
        return {
          isValid: false,
          message:
            "Document does not appear to be a valid student visa. Please check and upload the correct document.",
          details: {
            confidence: `${confidence}%`,
            primaryKeywordsFound: primaryMatches,
            secondaryKeywordsFound: secondaryMatches,
          },
        };
      }
    } catch (error) {
      return {
        isValid: true,
        message:
          "Student visa accepted with minimal verification due to processing error.",
        details: {
          error: error.message,
          note: "Automatic approval due to leniency policy",
        },
      };
    }
  };

  const verifyStudentRecords = async (documentData) => {
    try {
      const { textContent, pageCount } = documentData;

      if (!textContent) {
        return {
          isValid: false,
          message: 'Document is empty or unreadable. Please ensure you upload a valid document with visible content.',
          details: { error: 'No text content detected' },
        };
      }

      const textLower = textContent.toLowerCase().replace(/\s+/g, ' ').trim();

      const educationalKeywords = [
        'school', 'college', 'university', 'institute', 'high', 'education', 'secondary', 'academic',
        'transcript', 'academic record', 'grades', 'grade', 'marks', 'semester', 'term',
        'credits', 'units', 'course', 'subjects', 'subject', 'gpa', 'grade point',
        'certification', 'official', 'records', 'school year',
        'transfer', 'enrollment', 'registration', 'performance', 'evaluation',
        'mathematics', 'english', 'science', 'history', 'physics', 'chemistry',
        'biology', 'literature', 'economics', 'computer', 'programming',
        'passed', 'failed', 'incomplete', 'academic',
        'secondary', 'high school', 'general average',
        'filipino', 'araling panlipunan', 'mapeh', 'values education',
        'lrn', 'learner reference number', 'deped', 'department of education',
        'form 138', 'report card',
      ];

      const authenticationKeywords = [
        'authenticated', 'certified', 'notarized', 'apostille', 'approved',
        'embassy', 'consulate', 'ministry', 'foreign affairs', 'translation',
        'translator', 'official', 'attestation', 'verification',
      ];

      const coursePatterns = /[A-Z]{2,4}[-\s]?\d{3}/g;
      const gradePatterns = /([A-F][+-]?)|(100|\d{1,2}(\.\d+)?%?)|\b(excellent|good|satisfactory|pass|fail)\b/gi;

      const keywordMatches = educationalKeywords.filter((keyword) =>
        textLower.includes(keyword.toLowerCase())
      );
      const authMatches = authenticationKeywords.filter((keyword) =>
        textLower.includes(keyword.toLowerCase())
      );
      const courses = textLower.match(coursePatterns) || [];
      const grades = textLower.match(gradePatterns) || [];

      let confidence = 0;

      const keywordScore = Math.min(30, keywordMatches.length * 3);
      confidence += keywordScore;

      const authScore = Math.min(30, authMatches.length * 5);
      confidence += authScore;

      const courseScore = Math.min(20, courses.length * 2);
      confidence += courseScore;

      const gradeScore = Math.min(30, grades.length * 6);
      confidence += gradeScore;

      const pageScore = Math.min(10, (pageCount || 1) * 5);
      confidence += pageScore;

      if (confidence >= 50 || (keywordMatches.length > 0 && authMatches.length > 0)) {
        return {
          isValid: true,
          message: 'Student records verification successful.',
          details: {
            confidence: `${confidence}%`,
            keywordsFound: keywordMatches.length > 0 ? keywordMatches.slice(0, 5) : 'None',
            authKeywordsFound: authMatches.length > 0 ? authMatches.slice(0, 5) : 'None',
            coursesDetected: courses.length,
            gradesDetected: grades.length,
            pageCount: pageCount || 1,
          },
        };
      } else {
        return {
          isValid: false,
          message: 'Document does not appear to be a valid student record. Ensure it includes academic details like grades, subjects, and authentication markers.',
          details: {
            confidence: `${confidence}%`,
            keywordsFound: keywordMatches.length > 0 ? keywordMatches.slice(0, 5) : 'None',
            authKeywordsFound: authMatches.length > 0 ? authMatches.slice(0, 5) : 'None',
            coursesDetected: courses.length,
            gradesDetected: grades.length,
            keywordsNeeded: 'Missing key indicators like grades, subjects, or authentication markers.',
          },
        };
      }
    } catch (error) {
      console.error('Student records verification error:', error);
      return {
        isValid: true,
        message: 'Student records accepted with minimal verification due to processing error.',
        details: { error: error.message, note: 'Automatic approval due to leniency policy' },
      };
    }
  };

  const verifyDocument = async (file) => {
    setIsVerifying(true);

    try {
      const fileType = file.type;
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const fileSize = file.size;

      const validationResult = validateFileBasics(fileType, fileSize, fileExtension);
      if (!validationResult.valid) {
        setVerificationResult({
          isValid: false,
          message: validationResult.message,
        });
        onVerificationComplete({
          isValid: false,
          message: validationResult.message,
          details: null,
        });
        setIsVerifying(false);
        return;
      }

      let documentData;
      if (fileType === 'application/pdf') {
        documentData = await processPdfDocument(file);
      } else {
        documentData = await processImageDocument(file);
      }

      let verificationResult;
      switch (requirementType) {
        case 'birth_certificate':
          verificationResult = await verifyBirthCertificate(documentData);
          break;
        case 'good_moral':
          verificationResult = await verifyGoodMoral(documentData);
          break;
        case 'baptismal_or_letter':
          verificationResult = await verifyBaptismalOrLetter(documentData);
          break;
        case 'report_card':
          verificationResult = await verifyEducationalDocument(documentData);
          break;
        case 'student_visa':
          verificationResult = await verifyStudentVisa(documentData);
          break;
        case 'student_records':
          verificationResult = await verifyStudentRecords(documentData);
          break;
        default:
          verificationResult = {
            isValid: false,
            message: 'Unknown requirement type',
            details: null,
          };
          break;
      }

      if (
        verificationResult.details?.confidence &&
        parseFloat(verificationResult.details.confidence) < 50
      ) {
        logBorderlineCase(
          requirementType,
          verificationResult.details.confidence,
          verificationResult.details
        );
      }

      setVerificationResult(verificationResult);
      setVerificationDetails(verificationResult.details);
      onVerificationComplete(verificationResult);
    } catch (error) {
      console.error('Verification error:', error);
      const partialVerification = {
        isValid: true,
        message: `Document processed with limited verification due to error: ${error.message}. Please verify the document contents.`,
        details: {
          error: error.message,
          note: 'Automatic approval due to error leniency policy',
        },
      };

      setVerificationResult(partialVerification);
      setVerificationDetails(partialVerification.details);
      onVerificationComplete(partialVerification);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className='document-verification'>
      {isVerifying && (
        <div className='verification-loading'>Verifying document...</div>
      )}
      {!isVerifying && verificationResult && (
        <div
          className={`verification-result ${
            verificationResult.isValid ? 'valid' : 'invalid'
          }`}
        >
          <h4>
            {verificationResult.isValid
              ? 'Verification Successful'
              : 'Verification Failed'}
          </h4>
          <p>
            {verificationResult.message}
            {verificationDetails?.note?.includes('limited confidence') && (
              <span className='verification-warning'>
                {' '}
                Please confirm this is a valid document.
              </span>
            )}
          </p>
          {verificationDetails && (
            <details>
              <summary>Verification Details</summary>
              <pre>{JSON.stringify(verificationDetails, null, 2)}</pre>
            </details>
          )}
        </div>
      )}
      <div className='supported-formats'>
        <small>Supported formats: JPEG, PNG, PDF</small>
      </div>
    </div>
  );
};

// Main EnrollmentDocumentVerificationSystem Component
const EnrollmentDocumentVerificationSystem = () => {
  const [files, setFiles] = useState({});
  const [verificationResults, setVerificationResults] = useState({});

  const handleFileUpload = (requirementId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFiles((prev) => ({ ...prev, [requirementId]: file }));
    setVerificationResults((prev) => ({
      ...prev,
      [requirementId]: {
        status: 'pending',
        message: 'Verification in progress...',
      },
    }));
  };

  const handleVerificationComplete = (requirementId, result) => {
    setVerificationResults((prev) => ({
      ...prev,
      [requirementId]: {
        status: result.isValid ? 'verified' : 'failed',
        message: result.message,
        details: result.details,
      },
    }));
  };

  const requirementTypeMap = {
    birth_certificate: 'Birth Certificate (original and photocopy)',
    good_moral: 'Certificate of Good Moral from last school attended (original)',
    baptismal_or_letter: 'Baptismal Certificate (photocopy) or Non-Catholic Letter signed by parents/guardian',
    report_card: 'Gr 10 original copy of report card (Form 138)',
    student_visa: 'Student Visa',
    student_records: 'Duly authenticated student records',
  };

  const getVerificationType = (requirementName) => {
    const nameLower = requirementName.toLowerCase();
    if (
      nameLower.includes('birth certificate') ||
      nameLower.includes('psa') ||
      nameLower.includes('nso')
    ) {
      return 'birth_certificate';
    } else if (nameLower.includes('good moral')) {
      return 'good_moral';
    } else if (
      nameLower.includes('baptismal') ||
      nameLower.includes('non-catholic')
    ) {
      return 'baptismal_or_letter';
    } else if (nameLower.includes('student visa')) {
      return 'student_visa';
    } else if (
      nameLower.includes('student records') ||
      nameLower.includes('report card') ||
      nameLower.includes('form 138')
    ) {
      return 'student_records';
    }
    return null;
  };

  return {
    DocumentVerification,
    handleFileUpload,
    handleVerificationComplete,
    getVerificationType,
    requirementTypeMap,
    files,
    setFiles,
    verificationResults,
    setVerificationResults,
  };
};

export default EnrollmentDocumentVerificationSystem;